import { IonCol, IonList, IonRow } from '@ionic/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import {
  setChiefComplaint,
  setPatientActivity,
  setReasonForEncounter,
  setDrugsAndAlcohol,
  setDrugsAndAlcoholIndicators,
  saveCcHpi,
  setCcDuration,
  setCcDurationUnit,
  setAlsAssessmentRationale,
  setAlsAssessment,
  setDrugsAndAlcoholIndicatorsPNNV,
} from '../store/ccHpi.actions'
import { CcHpiConstants } from '../store/ccHpi.constants'
import { getCardiacArrestSelector, getHistorySelector, getSituationSelector } from '../selectors'
import { getNarrativeSelector } from '../../narrative/selectors'
import { IOption } from '../../../common/common.types'
import MultiSelect from '../../../components/multiSelect/MultiSelect.component'
import { getCcHpiOptionsSelector } from '../../options/selectors'
import { NarrativeConstants } from '../../narrative/store/narrative.constants'
import { setHpi } from '../../narrative/store/narrative.actions'
import { IPHIComponent } from '../../../components/phi/phi.component'
import { AccordianComponent } from '../../../components/accordian/Accordian.components'
import CardiacArrestContent from './cardiacArrest.content'
import { getDatapointsConfigSelector, getServicesBillingConfigSelector } from '../../configurations/selectors'
import InjuryDetails from '../InjuryDetails/InjuryDetails.component'
import MotorVehicleIncident from '../motorVehicleIncident/MotorVehicleIncidentContent.tsx/MotorVehicleIncident.component'
import TextArea from '../../../components/textArea/TextArea.component'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import SecondaryComplaintContent from './secondaryComplaint.content'
import OtherComplaintsContent from './otherComplaints.content'
import { IServicesBillingConfig, IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import Validation from '../../../components/validation/Validation.component'

const CcHpiContent: React.FC<IPHIComponent> = (props: IPHIComponent) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const situationState = useSelector(getSituationSelector)
  const {
    complaint,
    ccDuration,
    ccDurationUnit,
    patientActivity,
    secondaryComplaint,
    scDuration,
    otherComplaints,
    possibleInjury,
    alsAssessment,
    alsAssessmentRationale,
  } = situationState

  const historyState = useSelector(getHistorySelector)
  const { drugalc, drugalcIndicators, drugalcIndicatorsPNNV } = historyState

  const ccHpiCardiacArrestState = useSelector(getCardiacArrestSelector)

  const dataPointsConfig = useSelector(getDatapointsConfigSelector)
  const { DP_CARDIACARREST, DP_SECCOMPLAINT, DP_OTHER_COMPLAINT, DP_COMPDURATION } = dataPointsConfig

  const { PR_COMPDURATION }: IServiceConfig = useSelector(getServiceConfigSelector)

  const { ALS_ASSESS }: IServicesBillingConfig = useSelector(getServicesBillingConfigSelector)

  const narrativeState = useSelector(getNarrativeSelector)
  const { hpi } = narrativeState

  const ccHpiOptions = useSelector(getCcHpiOptionsSelector)
  const {
    patientActivityOptions,
    reasonForEncounterOptions,
    drugsAndAlcoholOptions,
    drugsAndAlcoholIndicatorsOptions,
    ccDurationUnitsOptions,
    alsAssessmentOptions,
  } = ccHpiOptions

  const chiefComplaintOnChange = (e: CustomEvent) => {
    dispatch(setChiefComplaint(e.detail.value))
  }

  const alsAssessmentRationaleOnChange = (e: CustomEvent) => {
    dispatch(setAlsAssessmentRationale(e.detail.value))
  }

  const setAlsAssessmentOnChange = (option: IOption) => {
    dispatch(setAlsAssessment(option))
  }

  const hpiOnChange = (e: CustomEvent) => {
    dispatch(setHpi(e.detail.value))
  }

  const setReasonForEncounterOnChange = (option: IOption) => {
    dispatch(setReasonForEncounter(option))
  }

  const setDrugsAndAlcoholOnChange = (option: IOption) => {
    dispatch(setDrugsAndAlcohol(option))
  }

  const setDurationOnChange = (numbers: string[]) => {
    dispatch(setCcDuration(numbers.join('')))
  }

  const setDurationUnitOnChange = (option: IOption) => {
    dispatch(setCcDurationUnit(option))
  }

  const removeUnkFields = Object.fromEntries(
    Object.entries(ccHpiCardiacArrestState).filter(([key]) => !key.startsWith('unk') && !key.startsWith('__'))
  )

  const hasCComplaintData = () => {
    return (
      !!complaint ||
      !!ccDuration ||
      !!alsAssessmentRationale ||
      !!ccDuration ||
      !!alsAssessment?.id ||
      !!ccDurationUnit?.id ||
      (patientActivity && patientActivity.length > 0)
    )
  }

  const hasSComplaintData = () => {
    return !!secondaryComplaint || !!scDuration
  }

  const hasOComplaintData = () => {
    const otherComplaintsWithData = otherComplaints.filter((oc) => oc.otherComplaint || oc.ocDuration)
    return otherComplaintsWithData.length > 0
  }

  const hasHpiData = () => {
    return (
      !!hpi ||
      !!possibleInjury ||
      !!drugalc ||
      (drugalcIndicators && drugalcIndicators.length > 0) ||
      !!drugalcIndicatorsPNNV
    )
  }

  const hasCardiacArrestAnyData = Object.values(removeUnkFields).some((value) =>
    Array.isArray(value) ? value.length > 0 : value
  )

  const cchContent = (
    <IonList>
      <IonRow>
        <IonCol className='picklist'>
          <TextArea
            dataTestID='ChiefComplaint'
            rows={3}
            cols={20}
            maxlength={255}
            value={complaint}
            validationId={''}
            onChangeAction={(e) => {
              chiefComplaintOnChange(e)
            }}
          />
          <Validation id={CcHpiConstants.SET_CHIEF_COMPLAINT} />
        </IonCol>
      </IonRow>

      {(DP_COMPDURATION === 1 || (DP_COMPDURATION === 2 && PR_COMPDURATION === 1) || ccDuration || ccDurationUnit) && (
        <>
          <IonRow>
            <IonCol className='picklist'>
              <NumberPad
                elementId='CCDuration'
                dataTestId='CCDuration'
                values={ccDuration ? ccDuration.split('') : []}
                label={t('ccHpi.duration')}
                onNumPadClick={setDurationOnChange}
              />
              <Validation id={CcHpiConstants.SET_CCDURATION} />
            </IonCol>
            <IonCol>
              <SingleSelect
                dataTestID='CCDurationUnit'
                options={ccDurationUnitsOptions}
                selectedOption={ccDurationUnit}
                listHeader={t('ccHpi.durationUnit')}
                label={t('ccHpi.durationUnit')}
                action={setDurationUnitOnChange}
              />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />
        </>
      )}

      {(ALS_ASSESS === 1 || alsAssessmentRationale) && (
        <IonRow>
          <IonCol className='picklist'>
            <TextArea
              label={t('ccHpi.alsAssessmentRationale')}
              dataTestID='ALSAssessmentRationale'
              rows={3}
              cols={20}
              maxlength={200}
              value={alsAssessmentRationale}
              validationId={CcHpiConstants.SET_ALS_ASSESSMENT_RATIONALE}
              onChangeAction={(e) => {
                alsAssessmentRationaleOnChange(e)
              }}
            />
          </IonCol>
        </IonRow>
      )}

      {(ALS_ASSESS === 1 || alsAssessment) && (
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='ALSAssessment'
              options={alsAssessmentOptions}
              selectedOption={alsAssessment}
              listHeader={t('ccHpi.alsAssessment')}
              label={t('ccHpi.alsAssessment')}
              action={setAlsAssessmentOnChange}
            />
          </IonCol>
        </IonRow>
      )}

      <IonRow>
        <IonCol className='picklist'>
          <MultiSelect
            dataTestID='PatientActivity'
            listHeader={t('ccHpi.patientActivity')}
            options={patientActivityOptions}
            savedValues={patientActivity}
            action={setPatientActivity}
            callback={saveCcHpi}
            label={t('ccHpi.patientActivity')}
          />
        </IonCol>
      </IonRow>

      <hr className='field-divider' />
    </IonList>
  )

  const hpiContent = (
    <IonList>
      <IonRow>
        <IonCol className='picklist'>
          <TextArea
            dataTestID='HPI'
            rows={3}
            cols={20}
            maxlength={4000}
            value={hpi}
            validationId={NarrativeConstants.SET_HPI}
            onChangeAction={(e) => {
              hpiOnChange(e)
            }}
          />
        </IonCol>
      </IonRow>

      <hr className='field-divider' />

      <IonRow>
        <IonCol className='picklist'>
          <SingleSelect
            dataTestID='ReasonForEncounter'
            options={reasonForEncounterOptions}
            selectedOption={possibleInjury}
            listHeader={t('ccHpi.reasonForEncounter')}
            label={t('ccHpi.reasonForEncounter')}
            action={setReasonForEncounterOnChange}
          />
        </IonCol>
      </IonRow>

      <hr className='field-divider' />

      <IonRow>
        <IonCol className='picklist'>
          <SingleSelect
            dataTestID='DrugsAndAlcohol'
            options={drugsAndAlcoholOptions}
            selectedOption={drugalc}
            listHeader={t('ccHpi.drugsAndAlcohol')}
            label={t('ccHpi.drugsAndAlcohol')}
            action={setDrugsAndAlcoholOnChange}
          />
        </IonCol>
      </IonRow>

      <hr className='field-divider' />

      <IonRow>
        <IonCol className='picklist'>
          <MultiSelect
            dataTestID='DrugsAndAlcoholIndicators'
            listHeader={t('ccHpi.drugsAndAlcoholIndicators')}
            options={drugsAndAlcoholIndicatorsOptions}
            savedValues={drugalcIndicators}
            action={setDrugsAndAlcoholIndicators}
            callback={saveCcHpi}
            label={t('ccHpi.drugsAndAlcoholIndicators')}
            savedPNNVValue={drugalcIndicatorsPNNV}
            pnnvAction={setDrugsAndAlcoholIndicatorsPNNV}
          />
        </IonCol>
      </IonRow>

      <hr className='field-divider' />
    </IonList>
  )

  return (
    <IonList>
      <AccordianComponent heading={t('ccHpi.chiefComplaint')} content={cchContent} isCheckMark={hasCComplaintData()} />

      {(DP_SECCOMPLAINT === 1 || hasSComplaintData()) && (
        <AccordianComponent
          heading={t('ccHpi.secondaryComplaint')}
          content={<SecondaryComplaintContent />}
          isCheckMark={hasSComplaintData()}
        />
      )}

      {(DP_OTHER_COMPLAINT === 1 || hasOComplaintData()) && (
        <AccordianComponent
          heading={t('ccHpi.otherComplaint')}
          content={<OtherComplaintsContent />}
          isCheckMark={hasOComplaintData()}
        />
      )}

      <AccordianComponent heading={t('ccHpi.hpi')} content={hpiContent} isCheckMark={hasHpiData()} />

      {DP_CARDIACARREST === 1 && (
        <AccordianComponent
          heading={t('ccHpi.cardiacArrest')}
          content={<CardiacArrestContent />}
          isCheckMark={hasCardiacArrestAnyData}
        />
      )}

      <InjuryDetails />

      <MotorVehicleIncident />
    </IonList>
  )
}

export default CcHpiContent
